import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  BusinessPartnerNavigationData,
  BusinessPartnerQueryParams,
  BusinessPartnerNavigationEvent
} from '../components/business-partner-navigation/interfaces/business-partner-navigation.interfaces';

/**
 * Business Partner Navigation Service
 * Centralizes navigation logic for Business Partner related screens
 * Provides programmatic navigation capabilities similar to DocumentNavigationService
 */
@Injectable({
  providedIn: 'root'
})
export class BusinessPartnerNavigationService {
  /** Base path for Business Partner screens */
  private readonly basePath: string[] = ['master', 'business-partner', 'business-partner'];

  private readonly dialog = inject(MatDialog);

  constructor(private router: Router) {}

  /**
   * Navigate to Business Partner list with optional filters
   * @param queryParams - Query parameters for filtering
   * @param openInNewTab - Whether to open in new tab (default: true)
   */
  navigateToBusinessPartnerList(
    queryParams: BusinessPartnerQueryParams = {},
    openInNewTab: boolean = true
  ): void {
    const urlTree = this.router.createUrlTree(this.basePath, { queryParams });

    if (openInNewTab) {
      const url = window.location.origin + this.router.serializeUrl(urlTree);
      window.open(url, '_blank');
    } else {
      // Same-tab navigation replaces the current page, so close any open dialog
      // (e.g. the territory customers modal) that would otherwise float on top.
      this.dialog.closeAll();
      this.router.navigateByUrl(urlTree);
    }
  }

  /**
   * Navigate to Business Partner list filtered by specific Business Partner
   * Uses the same pattern as InvoiceListComponent with businessPartnerIds query param
   * @param businessPartner - Business Partner data to filter by
   * @param additionalParams - Additional query parameters
   * @param openInNewTab - Whether to open in new tab (default: true)
   */
  navigateToBusinessPartnerDetail(
    businessPartner: BusinessPartnerNavigationData,
    additionalParams: BusinessPartnerQueryParams = {},
    openInNewTab: boolean = true
  ): void {
    let queryParams: BusinessPartnerQueryParams;

    const searchTerm = this.getSearchTerm(businessPartner);
    queryParams = {
      search: searchTerm,
      ...additionalParams
    };

    this.navigateToBusinessPartnerList(queryParams, openInNewTab);
  }

  /**
   * Navigate with pre-built navigation event
   * @param navigationEvent - Complete navigation event with data and params
   */
  navigateFromEvent(navigationEvent: BusinessPartnerNavigationEvent): void {
    this.navigateToBusinessPartnerDetail(
      navigationEvent.data,
      navigationEvent.queryParams,
      true // Always open in new tab for programmatic navigation
    );
  }

  /**
   * Create Business Partner URL without navigating
   * @param queryParams - Query parameters for the URL
   * @returns Complete URL string
   */
  createBusinessPartnerUrl(queryParams: BusinessPartnerQueryParams = {}): string {
    const urlTree = this.router.createUrlTree(this.basePath, { queryParams });
    return window.location.origin + this.router.serializeUrl(urlTree);
  }

  /**
   * Create Business Partner detail URL for specific business partner
   * @param businessPartner - Business Partner data
   * @param additionalParams - Additional query parameters
   * @returns Complete URL string
   */
  createBusinessPartnerDetailUrl(
    businessPartner: BusinessPartnerNavigationData,
    additionalParams: BusinessPartnerQueryParams = {}
  ): string {
    const searchTerm = this.getSearchTerm(businessPartner);
    const queryParams: BusinessPartnerQueryParams = {
      search: searchTerm,
      ...additionalParams
    };

    return this.createBusinessPartnerUrl(queryParams);
  }

  /**
   * Check if Business Partner is navigable
   * @param bp - Business Partner data
   * @returns True if navigable, false otherwise
   */
  isBusinessPartnerNavigable(bp: BusinessPartnerNavigationData): boolean {
    if (!bp) return false;

    // Check if we have any identifiable information
    const hasId = !!bp.id;
    const hasCode = !!bp.code;
    const hasExternalCode = !!bp.externalKeys?.code;
    const hasName = !!bp.name;

    return hasId || hasCode || hasExternalCode || hasName;
  }

  /**
   * Get description for navigation tooltip
   * @returns Description string for tooltip
   */
  getNavigationDescription(): string {
    return 'Ver en Socios de Negocio';
  }

  /**
   * Get the appropriate search term for a Business Partner
   * Prioritizes code over name for more accurate filtering
   * @param businessPartner - Business Partner data
   * @returns Search term string
   */
  private getSearchTerm(businessPartner: BusinessPartnerNavigationData): string {
    if (!businessPartner) return '';

    // Priority order: external code, code, name
    const externalCode = businessPartner.externalKeys?.code;
    const code = businessPartner.code;
    const name = businessPartner.name;

    return externalCode || code || name || '';
  }

  /**
   * Validate query parameters to ensure only valid ones are used
   * @param queryParams - Raw query parameters
   * @returns Validated and cleaned query parameters
   */
  validateQueryParams(queryParams: BusinessPartnerQueryParams): BusinessPartnerQueryParams {
    const validatedParams: BusinessPartnerQueryParams = {};

    // Only include defined values to avoid empty query params
    const validKeys: (keyof BusinessPartnerQueryParams)[] = [
      'companyId',
      'businessPartnerRolId',
      'businessPartnerTypeId',
      'subrouteId',
      'selectedCompanyIds',
      'selectedRol',
      'selectedCustomerType',
      'selectedTypeCodes',
      'selectedDivisionIds',
      'selectedStatuses',
      'search',
      'currentPage',
      'limitPerPage',
      'geolocationFilter',
      'sort',
      'sortDirection'
    ];

    for (const key of validKeys) {
      const value = queryParams[key];
      if (value !== undefined && value !== null && value !== '') {
        // For arrays, only include non-empty arrays
        if (Array.isArray(value) && value.length > 0) {
          validatedParams[key] = value;
        } else if (!Array.isArray(value)) {
          validatedParams[key] = value;
        }
      }
    }

    return validatedParams;
  }

  /**
   * Create a navigation event
   * @param data - Business Partner data
   * @param queryParams - Query parameters
   * @param source - Source of the navigation
   * @returns Navigation event object
   */
  createNavigationEvent(
    data: BusinessPartnerNavigationData,
    queryParams: BusinessPartnerQueryParams = {},
    source: 'soldTo' | 'shipTo' | 'general' = 'general'
  ): BusinessPartnerNavigationEvent {
    return {
      data,
      queryParams: this.validateQueryParams(queryParams),
      timestamp: new Date(),
      source
    };
  }
}